/**
 * Typed HTTP layer over the shared contracts. The SPA imports TYPES from @amplispot/contracts and never
 * decides visibility itself — it renders what the API returns (ADR §1.4, §3.1).
 *
 * Every call names its route id, so request bodies, query strings and responses are checked against the
 * contract at compile time (`RouteBody`, `RouteQuery`, `RouteResponse`).
 */
import {
  buildPath,
  routes,
  type ErrorBody,
  type FieldError,
  type RouteBody,
  type RouteId,
  type RouteQuery,
  type RouteResponse,
} from '@amplispot/contracts';

export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly body: ErrorBody | null,
  ) {
    super(body?.error.message ?? (status === 0 ? 'Could not reach the server. Check your connection and try again.' : `The server answered ${status}. Try again in a moment.`));
  }
  get code() {
    return this.body?.error.code;
  }
  /** Field errors keyed by their dotted path, for showing under the matching input. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const f of this.body?.error.field_errors ?? ([] as FieldError[])) out[f.path] = f.message;
    return out;
  }
}

/** Called when any request comes back 401 — the app sends the user to the sign-in screen. */
let onNoSession: (() => void) | null = null;
export const setNoSessionHandler = (fn: () => void) => {
  onNoSession = fn;
};

/** Called when a request comes back 403 must_change_password: the session signed in with a temporary password. */
let onMustChange: (() => void) | null = null;
export const setMustChangeHandler = (fn: () => void) => {
  onMustChange = fn;
};

interface CallOptions<K extends RouteId> {
  params?: Record<string, string>;
  query?: RouteQuery<K>;
  body?: RouteBody<K>;
  /** Row version for If-Match on PATCH/PUT (ADR §11.1). */
  ifMatch?: number;
  /** Creating POSTs send one so a retried click cannot create twice. */
  idempotencyKey?: string;
}

const qs = (query: object | undefined): string => {
  if (!query) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export async function call<K extends RouteId>(id: K, opts: CallOptions<K> = {}): Promise<RouteResponse<K>> {
  const route = routes[id];
  const url = buildPath(route.path, opts.params) + qs(opts.query as object | undefined);
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.ifMatch !== undefined) headers['if-match'] = `"${opts.ifMatch}"`;
  if (opts.idempotencyKey) headers['idempotency-key'] = opts.idempotencyKey;

  let res: Response;
  try {
    res = await fetch(url, {
      method: route.method,
      credentials: 'same-origin',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch {
    throw new ApiFailure(0, null);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ErrorBody | null;
    // Sign-out handles its own 401 (session already gone): it goes to a plain /sign-in, with no
    // redirect back to the page the user was deliberately leaving.
    // A wrong password (401 invalid_credentials) is an answer about the password, not a lost session.
    if (res.status === 401 && onNoSession && id !== 'auth.logout' && body?.error.code !== 'invalid_credentials') onNoSession();
    if (res.status === 403 && body?.error.code === 'must_change_password') onMustChange?.();
    throw new ApiFailure(res.status, body);
  }
  if (res.status === 204) return undefined as RouteResponse<K>;
  return (await res.json()) as RouteResponse<K>;
}

/** Follows `next_cursor` until the last page, for lists a screen needs whole (counts, grouping). */
export async function callAll<K extends 'clients.list' | 'users.list'>(
  id: K,
  query: RouteQuery<K>,
): Promise<RouteResponse<K>['data']> {
  type Item = RouteResponse<K>['data'][number];
  const out: Item[] = [];
  let cursor: string | undefined;
  do {
    const q = { ...(query as object), limit: 200, cursor } as RouteQuery<K>;
    const page = (await call(id, { query: q })) as { data: Item[]; next_cursor: string | null };
    out.push(...page.data);
    cursor = page.next_cursor ?? undefined;
  } while (cursor);
  return out as RouteResponse<K>['data'];
}

export const newIdempotencyKey = (): string => crypto.randomUUID();
